import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Subject, merge } from 'rxjs';
import { filter, map, take, takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import {
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  baseInquiryActions,
  baseSharedActions,
  baseSharedSelectors,
  CustomerDepartmentQualificationsListItemResponse,
  DepartmentListItemResponse,
  EditInquiryFormInterface,
  ENVIRONMENTER,
  GroupListItemResponse,
  RequirementResponse,
  UserItemResponse,
  InquiryCreateRequest,
  baseInquirySelectors,
  ShiftPlanCalendarRangeInterface,
  BaseSimilarInquiriesService,
  UserItemFullNameType,
} from 'base';

@Component({
  selector: 'base-customer-shift-plan-sidebar',
  templateUrl: './base-customer-shift-plan-sidebar.component.html',
})
export class BaseCustomerShiftPlanSidebarComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('content', { static: true }) sidebarContent: ElementRef;
  @Input() editInquiryForm: FormGroup<EditInquiryFormInterface>;
  @Input() isSidebarVisible = false;
  @Input() inquiryData: InquiryCreateRequest;
  @Input() submitted = false;
  @Input() isEmployeeShiftPlan: boolean;
  @Input() inquiredEmployeeName: string;
  @Input() inquiryId?: number;
  @Output() submittedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() toggleSidebar = new EventEmitter<void>();

  users: Array<UserItemFullNameType> = [];
  qualifications: CustomerDepartmentQualificationsListItemResponse[] | undefined;
  filteredRequirements: RequirementResponse[];
  requirements: RequirementResponse[];
  shifts = [
    { icon: 'fa-sunrise', control: 'fd', startTime: '', endTime: '' },
    { icon: 'fa-sun-bright', control: 'zd', startTime: '', endTime: '' },
    { icon: 'fa-clock', control: 'sd', startTime: '', endTime: '' },
    { icon: 'fa-moon-stars', control: 'nd', startTime: '', endTime: '' },
  ];

  baseAvatarSize = BaseAvatarSize;
  buttonSize = BaseButtonSize;
  departments: DepartmentListItemResponse[] | undefined;
  filteredQualifications: CustomerDepartmentQualificationsListItemResponse[] | undefined;
  person: Partial<UserItemResponse & { id: number; fullName: string }> | null | undefined = null;
  isInitialContactPerson = true;
  isInitialDepartment = true;
  sidebarBreakpoint = 2000;
  selectedRanges: ShiftPlanCalendarRangeInterface[] = [];

  private destroy$ = new Subject<boolean>();

  constructor(
    @Inject(ENVIRONMENTER) private environment: any,
    private store: Store<BaseCoreState>,
    private offcanvasService: NgbOffcanvas,
    private baseSimilarInquiriesService: BaseSimilarInquiriesService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(
      baseSharedActions.loadCustomerDepartmentQualificationsList({
        payload: { department: this.inquiryData?.customerDepartment, tag: this.environment.application.tag },
      })
    );
    this.setDepartments();
    this.setRequirements();
    this.fillForms();
    if (this.isInitialContactPerson) {
      this.setContactPerson();
    }

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data)
      )
      .subscribe((ranges) => {
        this.selectedRanges = ranges;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.isSidebarVisible) {
      if (!!changes.isSidebarVisible.currentValue) {
        this.openInfoOffcanvas(this.sidebarContent);
      } else if (this.editInquiryForm.valid) {
        this.offcanvasService.dismiss();
      }
    }
  }

  ngAfterViewInit() {
    this.setQualifications();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.isSidebarVisible) {
      if ((event.target as Window).innerWidth > this.sidebarBreakpoint) {
        this.offcanvasService.dismiss();
      }
    }
  }

  openInfoOffcanvas(content: any): void {
    this.offcanvasService
      .open(content, {
        ariaLabelledBy: 'offcanvas-basic-title',
        position: 'end',
        panelClass: 'right-content-offcanvas',
      })
      .result.then(
        () => {},
        () => {
          this.toggleSidebar.emit();
        }
      );
  }

  handleCustomerDepartment(): void {
    this.editInquiryForm.controls.customerDepartment.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((value: DepartmentListItemResponse | null) => {
        if (value) {
          this.changeCustomerDepartment(value.id);
        }
      });
  }

  changeCustomerDepartment(departmentId: number): void {
    this.store.dispatch(baseSharedActions.getCustomerDepartmentUsers({ payload: departmentId }));
    this.store.dispatch(
      baseSharedActions.loadCustomerDepartmentQualificationsList({
        payload: { department: departmentId, tag: this.environment.application.tag },
      })
    );
    this.store.dispatch(baseInquiryActions.fetchDepartment({ payload: departmentId }));
  }

  handleContactPerson(): void {
    this.editInquiryForm.controls.responsible.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: UserItemFullNameType | null) => {
        if (value) {
          this.person = value;
        }
      });
  }

  removeContactPerson() {
    this.editInquiryForm.controls.responsible.reset();
  }

  private fillForms(): void {
    this.handleCustomerDepartment();
    this.handleContactPerson();
    this.handleQualifications();
  }

  private setQualifications(): void {
    this.store
      .select(baseSharedSelectors.getCustomerDepartmentQualificationsList)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as CustomerDepartmentQualificationsListItemResponse[])
      )
      .subscribe((qualificationsListResponse) => {
        if (this.inquiryData && this.isInitialDepartment) {
          this.qualifications = qualificationsListResponse;
          this.filteredQualifications = qualificationsListResponse;

          const mainQualification = this.qualifications?.find(
            (qualification) => qualification.id === this.inquiryData.mainQualification
          );

          const department = (this.departments || []).find(
            (department) => department.id === this.inquiryData.customerDepartment
          );

          this.editInquiryForm.patchValue({
            jobDescription: this.inquiryData.jobDescription,
            qualifications: this.inquiryData.qualifications || [],
            costUnit: this.inquiryData.costUnit,
            workEquipmentCustomer: this.inquiryData.workEquipmentCustomer,
            workEquipmentPsp: this.inquiryData.workEquipmentPsp,
            remark: this.inquiryData.remark,
            document: this.inquiryData.document,
            requirements: this.inquiryData.requirements || [],
            weeklyWorkingHours: this.inquiryData.weeklyWorkingHours || '',
            customerDepartment: department || null,
            mainQualification: mainQualification || null,
            timeShiftsForm: this.inquiryData.shiftTimes,
          });

          if (window.innerWidth <= this.sidebarBreakpoint && !this.editInquiryForm.valid) {
            this.toggleSidebar.emit();
          }
          this.isInitialDepartment = false;
        }
      });
  }

  private setContactPerson(): void {
    this.store
      .select(baseSharedSelectors.getDepartmentUsers)
      .pipe(
        takeUntil(this.destroy$),
        filter((departmentUsersState) => !!departmentUsersState.ok),
        map((response) => response.data as UserItemResponse[])
      )
      .subscribe((users) => {
        if (users) {
          users = users.map((user) => ({ ...user }));
          this.users = users.map((user: any) => {
            user.fullName = `${user.firstName} ${user.lastName}`;

            return user;
          });
        }
        if (this.isInitialContactPerson) {
          this.person = this.users.find((user) => user.party === this.inquiryData?.responsible);
          const responsibleValue: UserItemFullNameType = {
            active: this.person?.active ?? false,
            avatar: this.person?.avatar || '',
            email: this.person?.email || '',
            firstName: this.person?.firstName || '',
            gender: this.person?.gender as number,
            group: this.person?.group as GroupListItemResponse,
            invited: this.person?.invited ?? false,
            lastName: this.person?.lastName || '',
            party: this.person?.party as number,
            position: this.person?.position || '',
            fullName: this.person?.fullName || '',
            roles: this.person?.roles || [],
          };

          this.editInquiryForm.controls.responsible.setValue(responsibleValue);
          this.isInitialContactPerson = false;
        }
      });
  }

  private setRequirements(): void {
    this.store
      .select(baseSharedSelectors.getRequirements)
      .pipe(
        takeUntil(this.destroy$),
        map((response) => response.data)
      )
      .subscribe((requirements) => {
        const reqs = [];
        if (!requirements) {
          return;
        }

        for (const category of requirements) {
          for (const r of category.requirements) {
            reqs.push(r);
          }
        }
        if (requirements) {
          this.filteredRequirements = reqs;
          this.requirements = reqs;
        }
      });
  }

  private setDepartments(): void {
    this.store
      .select(baseSharedSelectors.getDepartments)
      .pipe(
        take(1),
        map((response) => response.data)
      )
      .subscribe((departments) => {
        this.departments = departments;
      });
  }

  private handleQualifications() {
    merge(
      this.editInquiryForm.controls.mainQualification.valueChanges.pipe(distinctUntilChanged()),
      this.editInquiryForm.controls.qualifications.valueChanges.pipe(distinctUntilChanged()),
      this.editInquiryForm.controls.customerDepartment.valueChanges.pipe(distinctUntilChanged())
    )
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.loadSimilarInquiries(this.selectedRanges);
      });
  }

  private loadSimilarInquiries(ranges: ShiftPlanCalendarRangeInterface[]) {
    this.baseSimilarInquiriesService.loadSimilarInquiries(this.editInquiryForm.getRawValue(), ranges);
  }
}
